import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// JS weekday convention: 0=Sun … 6=Sat. Saudi work week starts Sunday.
const WEEKDAYS = [
  { v: 0, l: "Sunday" }, { v: 1, l: "Monday" }, { v: 2, l: "Tuesday" },
  { v: 3, l: "Wednesday" }, { v: 4, l: "Thursday" }, { v: 5, l: "Friday" }, { v: 6, l: "Saturday" },
];

export default function AvailabilityEditor() {
  const [weekly, setWeekly] = useState({});      // { weekday: [{start,end}] }
  const [overrides, setOverrides] = useState([]); // [{date, available, windows:[{start,end}]}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/availability/me").then((r) => {
      const wk = {};
      (r.data.weekly || []).forEach((rule) => { wk[rule.weekday] = rule.windows || []; });
      setWeekly(wk);
      setOverrides(r.data.overrides || []);
    }).catch(() => {});
  }, []);

  function addWindow(wd) {
    setWeekly((p) => ({ ...p, [wd]: [...(p[wd] || []), { start: "09:00", end: "12:00" }] }));
  }
  function updateWindow(wd, idx, key, val) {
    setWeekly((p) => ({ ...p, [wd]: p[wd].map((w, i) => (i === idx ? { ...w, [key]: val } : w)) }));
  }
  function removeWindow(wd, idx) {
    setWeekly((p) => ({ ...p, [wd]: p[wd].filter((_, i) => i !== idx) }));
  }

  function addOverride() {
    setOverrides((p) => [...p, { date: "", available: false, windows: [] }]);
  }
  function updateOverride(idx, patch) {
    setOverrides((p) => p.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }
  function removeOverride(idx) {
    setOverrides((p) => p.filter((_, i) => i !== idx));
  }

  async function save() {
    const weeklyArr = Object.entries(weekly)
      .filter(([, wins]) => wins && wins.length > 0)
      .map(([wd, wins]) => ({ weekday: Number(wd), windows: wins }));
    const ov = overrides.filter((o) => o.date);
    setSaving(true);
    try {
      await api.put("/availability/me", { weekly: weeklyArr, overrides: ov });
      toast.success("Availability saved");
      window.dispatchEvent(new CustomEvent("availability-saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="hah-card p-6" data-testid="availability-editor">
      <h3 className="text-base font-semibold text-[#1F2937] mb-1">Weekly work availability</h3>
      <p className="text-xs text-[#6B7280] mb-4">Set the hours you're available for visits and teleconsultations. Assignments (manual and via the Dispatch Orchestrator) will only place you within these active windows. Days with no window are treated as off.</p>

      <div className="space-y-3">
        {WEEKDAYS.map((d) => (
          <div key={d.v} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`weekday-${d.v}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#1F2937]">{d.l}</span>
              <Button size="sm" variant="outline" className="border-[#E5E7EB] h-7" onClick={() => addWindow(d.v)} data-testid={`add-window-${d.v}`}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add window
              </Button>
            </div>
            {(weekly[d.v] || []).length === 0 ? (
              <div className="text-xs text-[#9CA3AF] italic mt-2">Unavailable</div>
            ) : (
              <div className="mt-2 space-y-2">
                {(weekly[d.v] || []).map((w, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input type="time" value={w.start} onChange={(e) => updateWindow(d.v, idx, "start", e.target.value)} className="w-32 h-8" data-testid={`window-${d.v}-${idx}-start`} />
                    <span className="text-[#6B7280] text-sm">to</span>
                    <Input type="time" value={w.end} onChange={(e) => updateWindow(d.v, idx, "end", e.target.value)} className="w-32 h-8" data-testid={`window-${d.v}-${idx}-end`} />
                    <button onClick={() => removeWindow(d.v, idx)} className="text-[#EF4444] hover:bg-red-50 rounded p-1" data-testid={`remove-window-${d.v}-${idx}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-[#E5E7EB] pt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-[#1F2937]">Date-specific overrides</h4>
            <p className="text-xs text-[#6B7280]">Block a day off or set special hours for a specific date.</p>
          </div>
          <Button size="sm" variant="outline" className="border-[#E5E7EB] h-7" onClick={addOverride} data-testid="add-override"><Plus className="w-3.5 h-3.5 mr-1" /> Add date</Button>
        </div>
        <div className="space-y-2">
          {overrides.map((o, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-wrap border border-[#E5E7EB] rounded-lg p-2" data-testid={`override-${idx}`}>
              <Input type="date" value={o.date} onChange={(e) => updateOverride(idx, { date: e.target.value })} className="w-40 h-8" data-testid={`override-${idx}-date`} />
              <label className="flex items-center gap-1.5 text-sm text-[#374151]">
                <input type="checkbox" checked={o.available} onChange={(e) => updateOverride(idx, { available: e.target.checked, windows: e.target.checked && o.windows.length === 0 ? [{ start: "09:00", end: "12:00" }] : o.windows })} data-testid={`override-${idx}-available`} />
                Available
              </label>
              {o.available && (o.windows || []).map((w, wi) => (
                <span key={wi} className="flex items-center gap-1">
                  <Input type="time" value={w.start} onChange={(e) => updateOverride(idx, { windows: o.windows.map((x, j) => j === wi ? { ...x, start: e.target.value } : x) })} className="w-28 h-8" />
                  <span className="text-xs">to</span>
                  <Input type="time" value={w.end} onChange={(e) => updateOverride(idx, { windows: o.windows.map((x, j) => j === wi ? { ...x, end: e.target.value } : x) })} className="w-28 h-8" />
                </span>
              ))}
              {!o.available && <span className="text-xs text-[#EF4444] font-medium">Day off</span>}
              <button onClick={() => removeOverride(idx)} className="text-[#EF4444] hover:bg-red-50 rounded p-1 ml-auto" data-testid={`remove-override-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="availability-save">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save availability"}
        </Button>
      </div>
    </div>
  );
}
